// El medio de pago y sus etiquetas son exactamente los mismos que en gastos
// e ingresos, así que se reutilizan `MEDIOS_PAGO` / `formatearMedioPago`
// desde `src/utils/gastos.js` en vez de duplicarlos aquí. Lo mismo aplica
// para `obtenerEstadoPago` (pendiente / parcial / pagado).
import { MEDIOS_PAGO } from './gastos'
import { formatearMoneda } from './numeros'

// Orden de medios de pago específico del POS (Nueva venta, Registrar pago).
// Usa los mismos valores/etiquetas de `MEDIOS_PAGO` pero en el orden que
// pide la experiencia de pagos múltiples del POS.
export const OPCIONES_MEDIO_PAGO_POS = [
  'efectivo',
  'transferencia',
  'cheque',
  'tarjeta_debito',
  'tarjeta_credito',
  'otro',
].map((valor) => ({ valor, etiqueta: MEDIOS_PAGO[valor] }))

// Reutiliza el helper central de numeros.js — ver ahí la regla de
// presentación (oculta ,00 pero nunca oculta centavos reales).
export function formatearMontoPos(monto) {
  return formatearMoneda(monto)
}

export function formatearKilosPos(valor) {
  const numero = Number(valor)

  if (!Number.isFinite(numero)) return '0 kg'

  return `${numero.toLocaleString('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  })} kg`
}

export function formatearFechaPos(fechaTexto) {
  if (!fechaTexto) return '—'

  const [anio, mes, dia] = fechaTexto.split('-')

  if (!anio || !mes || !dia) return '—'

  return `${dia}-${mes}-${anio}`
}

// Inversa de formatearFechaPos: arma un Date en horario local a partir de
// 'YYYY-MM-DD' (para exportar a Excel como fecha real, no como texto).
// `new Date('YYYY-MM-DD')` interpretaría el string como UTC y correría un
// día hacia atrás en zonas horarias negativas — por eso se arma con los
// componentes numéricos, igual criterio que el resto de este archivo.
export function parsearFechaLocalPos(fechaTexto) {
  if (!fechaTexto) return null

  const [anio, mes, dia] = fechaTexto.split('-').map(Number)

  if (!anio || !mes || !dia) return null

  return new Date(anio, mes - 1, dia)
}

export function obtenerFechaActualPos() {
  const fecha = new Date()
  const anio = fecha.getFullYear()
  const mes = String(fecha.getMonth() + 1).padStart(2, '0')
  const dia = String(fecha.getDate()).padStart(2, '0')

  return `${anio}-${mes}-${dia}`
}

// Usa componentes locales (año/mes/día) en vez de toISOString para evitar
// el corrimiento de un día que produce la conversión a UTC según la zona
// horaria del navegador — mismo criterio que obtenerFechaActualPos.
export function formatearFechaISOPos(fecha) {
  const anio = fecha.getFullYear()
  const mes = String(fecha.getMonth() + 1).padStart(2, '0')
  const dia = String(fecha.getDate()).padStart(2, '0')

  return `${anio}-${mes}-${dia}`
}

// Accesos rápidos de fecha para los filtros de Ventas POS: solo calculan
// el rango { desde, hasta } — no ejecutan ninguna consulta adicional, el
// filtrado sigue pasando por el mismo `fecha_venta` de siempre.
export function obtenerRangoFechaHoyPos() {
  const hoy = obtenerFechaActualPos()

  return { desde: hoy, hasta: hoy }
}

export function obtenerRangoFechaSemanaActualPos() {
  const ahora = new Date()
  const diaSemana = ahora.getDay()
  const diferenciaLunes = diaSemana === 0 ? 6 : diaSemana - 1
  const lunes = new Date(
    ahora.getFullYear(),
    ahora.getMonth(),
    ahora.getDate() - diferenciaLunes
  )

  return {
    desde: formatearFechaISOPos(lunes),
    hasta: obtenerFechaActualPos(),
  }
}

export function obtenerRangoFechaMesActualPos() {
  const ahora = new Date()
  const primerDia = new Date(ahora.getFullYear(), ahora.getMonth(), 1)

  return {
    desde: formatearFechaISOPos(primerDia),
    hasta: obtenerFechaActualPos(),
  }
}

export function obtenerEstadoVentaPos(valor) {
  if (valor === 'anulado') {
    return {
      etiqueta: 'Eliminada',
      clase: 'bg-red-50 text-red-600',
    }
  }

  return {
    etiqueta: 'Registrado',
    clase: 'bg-emerald-100 text-emerald-700',
  }
}

// Estado de pago derivado en vivo mientras se arman los pagos de una venta
// nueva (antes de registrarla). El backend recalcula el estado real
// (pendiente/parcial/pagado) al guardar; esto es solo para retroalimentar
// al usuario en el formulario.
export function obtenerEstadoPagoNuevaVentaPos(totalVenta, totalPagado) {
  if (totalPagado <= 0) {
    return {
      etiqueta: 'Pago pendiente',
      clase: 'bg-amber-100 text-amber-700',
    }
  }

  if (totalPagado >= totalVenta) {
    return {
      etiqueta: 'Venta pagada completamente',
      clase: 'bg-emerald-100 text-emerald-700',
    }
  }

  return {
    etiqueta: 'Pago parcial',
    clase: 'bg-blue-100 text-blue-700',
  }
}

export const ESTADOS_ENVASES_POS = {
  pendiente: {
    etiqueta: 'Pendiente',
    clase: 'bg-amber-100 text-amber-700',
  },
  pagado: {
    etiqueta: 'Pagado',
    clase: 'bg-emerald-100 text-emerald-700',
  },
}

export function obtenerEstadoEnvasesPos(valor) {
  return ESTADOS_ENVASES_POS[valor] || ESTADOS_ENVASES_POS.pendiente
}

// Espejo únicamente informativo de las reglas que Supabase ya aplica en
// `editar_venta_pos` (estado registrado, dueño de la venta o
// administrador). Una venta con pagos (pendiente/parcial/pagada) SÍ se
// puede editar — Supabase valida aparte que el nuevo total no quede por
// debajo de lo ya pagado. Sirve para no ofrecer el botón Editar cuando
// de todas formas la RPC lo va a rechazar, pero Supabase sigue siendo la
// autoridad final: el frontend nunca reemplaza esa validación.
export function puedeEditarVentaPos(venta, { esAdministrador, usuarioId }) {
  if (!venta) return false
  if (venta.estado !== 'registrado') return false
  if (esAdministrador) return true

  return Boolean(usuarioId) && venta.creado_por === usuarioId
}

function etiquetaDescuentoHistorial(venta) {
  const tipo = venta?.tipo_descuento || 'ninguno'

  if (tipo === 'porcentaje') {
    return `${Number(venta.valor_descuento) || 0}%`
  }

  if (tipo === 'monto') {
    return formatearMontoPos(venta.valor_descuento)
  }

  return 'Sin descuento'
}

// Compara los snapshots `datos_anteriores`/`datos_nuevos` que guarda
// `vw_ventas_pos_historial` (formato { venta, detalles, pagos }) y arma una
// lista de cambios simples de mostrar. No compara productos línea por línea
// para mantenerlo simple: solo campos escalares de la venta.
export function compararCambiosVentaPos(datosAnteriores, datosNuevos) {
  const antes = datosAnteriores?.venta || {}
  const despues = datosNuevos?.venta || {}
  const cambios = []

  if (antes.fecha_venta !== despues.fecha_venta) {
    cambios.push({
      etiqueta: 'Fecha',
      antes: formatearFechaPos(antes.fecha_venta),
      despues: formatearFechaPos(despues.fecha_venta),
    })
  }

  if (antes.estado_envases !== despues.estado_envases) {
    cambios.push({
      etiqueta: 'Cajas/Rejillas',
      antes: obtenerEstadoEnvasesPos(antes.estado_envases).etiqueta,
      despues: obtenerEstadoEnvasesPos(despues.estado_envases)
        .etiqueta,
    })
  }

  if (
    Number(antes.monto_envases_pagado) !==
    Number(despues.monto_envases_pagado)
  ) {
    cambios.push({
      etiqueta: 'Monto Cajas/Rejillas',
      antes: formatearMontoPos(antes.monto_envases_pagado),
      despues: formatearMontoPos(despues.monto_envases_pagado),
    })
  }

  if (
    antes.tipo_descuento !== despues.tipo_descuento ||
    Number(antes.valor_descuento) !== Number(despues.valor_descuento)
  ) {
    cambios.push({
      etiqueta: 'Descuento',
      antes: etiquetaDescuentoHistorial(antes),
      despues: etiquetaDescuentoHistorial(despues),
    })
  }

  if (
    Number(antes.subtotal_venta) !== Number(despues.subtotal_venta)
  ) {
    cambios.push({
      etiqueta: 'Subtotal',
      antes: formatearMontoPos(antes.subtotal_venta),
      despues: formatearMontoPos(despues.subtotal_venta),
    })
  }

  if (Number(antes.total_venta) !== Number(despues.total_venta)) {
    cambios.push({
      etiqueta: 'Total',
      antes: formatearMontoPos(antes.total_venta),
      despues: formatearMontoPos(despues.total_venta),
    })
  }

  if (Number(antes.monto_pagado) !== Number(despues.monto_pagado)) {
    cambios.push({
      etiqueta: 'Monto pagado',
      antes: formatearMontoPos(antes.monto_pagado),
      despues: formatearMontoPos(despues.monto_pagado),
    })
  }

  if ((antes.observaciones || '') !== (despues.observaciones || '')) {
    cambios.push({
      etiqueta: 'Observaciones',
      antes: antes.observaciones || '—',
      despues: despues.observaciones || '—',
    })
  }

  return cambios
}
